import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache, HttpLink } from "@apollo/client";
import { Observable } from "@apollo/client/utilities";

import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
// import { useEffect } from "react";

import { AppRoutes } from "./Components";
import { AuthContext } from './Context';
import { ChatWidget } from './Components';




Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: window.ENV.USER_POOL_ID,
      userPoolClientId: window.ENV.USER_POOL_CLIENT_ID,
      region: window.ENV.REGION,
    }
  }
});



const getCurrentSession = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (err) {
    console.error(err);
    return false;
  }
}

// Map operations to their respective API Gateway endpoints
const OPERATION_TO_ENDPOINT = {
  // Announcements API
  'queryAnnouncements': '/announcements',
  'getAnnouncement': '/announcements',
  'getAnnouncements': '/announcements',
  'addAnnouncement': '/announcements',
  'updateAnnouncement': '/announcements',
  'deleteAnnouncement': '/announcements',
  
  // Members API
  'getMember': '/members',
  'adminGetMember': '/members',
  'getMembersByLastInitial': '/members',
  'downloadMemberList': '/members',
  'adminResetMemberPassword': '/members',
  'addMember': '/members',
  'updateMember': '/members',
  'deleteMember': '/members',
  'resendMemberWelcomeEmail': '/members',
  'resetMemberSignupStatus': '/members',
  
  // Newsletters API
  'getNewsletter': '/newsletters',
  'getNewsletters': '/newsletters',
  'queryPublishedNewsletters': '/newsletters',
  'queryDocumentsByType': '/newsletters',
  'addNewsletter': '/newsletters',
  'updateNewsletter': '/newsletters',
  'deleteNewsletter': '/newsletters',
  
  // Site Data API
  'getSiteData': '/sitedata',
  'getWeatherData': '/sitedata',
  'GetWeatherData': '/sitedata',
  'setSiteDataShotgunRangeStatus': '/sitedata',
  'setSiteDataRiflePistolEastRangeStatus': '/sitedata',
  'setSiteDataRiflePistolCentralRangeStatus': '/sitedata',
  'setSiteDataRiflePistolWestRangeStatus': '/sitedata',
  'setSiteDataRiflePistolRangeGateStatus': '/sitedata',
  'setGateCode': '/sitedata',
  'getRangecamToken': '/sitedata',
  'getUserPoolClientId': '/sitedata',
  
  // Email API
  'sendBlastEmail': '/email',
  'SendBlastEmail': '/email',
  'createDistributionList': '/email',
  'CreateDistributionList': '/email',
  'updateDistributionList': '/email',
  'UpdateDistributionList': '/email',
  'deleteDistributionList': '/email',
  'DeleteDistributionList': '/email',
  'addEmailsToDistributionList': '/email',
  'AddEmailsToDistributionList': '/email',
  'removeEmailsFromDistributionList': '/email',
  'RemoveEmailsFromDistributionList': '/email',
  'getDistributionList': '/email',
  'GetDistributionList': '/email',
  'listDistributionLists': '/email',
  'ListDistributionLists': '/email',
  
  // Match Management API
  'getMatchFormat': '/matches',
  'GetMatchFormat': '/matches',
  'listMatchFormats': '/matches',
  'ListMatchFormats': '/matches',
  'createMatchFormat': '/matches',
  'CreateMatchFormat': '/matches',
  'updateMatchFormat': '/matches',
  'UpdateMatchFormat': '/matches',
  'deleteMatchFormat': '/matches',
  'DeleteMatchFormat': '/matches',
  'getMatch': '/matches',
  'GetMatch': '/matches',
  'queryMatches': '/matches',
  'QueryMatches': '/matches',
  'createMatch': '/matches',
  'CreateMatch': '/matches',
  'updateMatch': '/matches',
  'UpdateMatch': '/matches',
  'deleteMatch': '/matches',
  'DeleteMatch': '/matches',
  'getMatchResults': '/matches',
  'GetMatchResults': '/matches',
  'addMatchResult': '/matches',
  'AddMatchResult': '/matches',
  'updateMatchResult': '/matches',
  'UpdateMatchResult': '/matches',
  'deleteMatchResult': '/matches',
  'DeleteMatchResult': '/matches',
  'bulkAddMatchResults': '/matches',
  'BulkAddMatchResults': '/matches',
  'finalizeMatchResults': '/matches',
  'FinalizeMatchResults': '/matches',
  'getParticipantHistory': '/matches',
  'GetParticipantHistory': '/matches',
};

// Dynamic routing link that selects the correct endpoint based on operation
const routingLink = new ApolloLink((operation, forward) => {
  const operationName = operation.operationName;
  const endpoint = OPERATION_TO_ENDPOINT[operationName];
  
  if (!endpoint) {
    console.warn(`No endpoint mapping found for operation: ${operationName}, using /announcements as default`);
    operation.setContext({ uri: '/announcements' });
  } else {
    operation.setContext({ uri: endpoint });
  }
  
  return forward(operation);
});

// Create HTTP link that uses the URI from context
const httpLink = new HttpLink({
  uri: (operation) => operation.getContext().uri || '/announcements'
});

// Add Cognito auth headers for authenticated users
const authLink = new ApolloLink((operation, forward) => {
  return new Observable(observer => {
    let handle;
    Promise.resolve(operation)
      .then(async (operation) => {
        // Try to get auth token and add it if available
        const token = await getCurrentSession();
        if (token) {
          operation.setContext({
            headers: {
              authorization: `Bearer ${token}`,
            }
          });
        }
        // If no token, AppSync will use API key authentication
      })
      .then(() => {
        handle = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch(observer.error.bind(observer));

    return () => {
      if (handle) handle.unsubscribe();
    };
  });
});

// Chain the links: routing -> auth -> http
const awsLink = ApolloLink.from([routingLink, authLink, httpLink]);

const cache = new InMemoryCache();

export const client = new ApolloClient({
  link: awsLink,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
})

export const App = () => {
  // useEffect(() => {
  //   async function checkUserLoggedIn() {
  //     try {
  //       const user = await Auth.currentAuthenticatedUser();
  //       if (user) {
  //         console.log(user);
  //       }
  //     } catch (e) {
  //       console.error(e);
  //     }
  //   }
  //   checkUserLoggedIn();
  // }, []);

  return (
    <ApolloProvider client={client}>
      <AuthContext>
        <AppRoutes />
        <ChatWidget />
      </AuthContext>
    </ApolloProvider>
  );
};
